import { useState } from "react";
import {
  FieldValues,
  Path,
  RegisterOptions,
  useFormContext,
  UseFormRegister
} from "react-hook-form";

import { CloseIcon, EyeCloseIcon, EyeOpenIcon } from "@/assets/icons";

import { twMerge } from "tailwind-merge";

export interface FormFieldProps<T extends FieldValues> {
  isAreaInput?: boolean;
  placeHolder: string;
  label?: string;
  inputClassName?: string;
  name: Path<T>;
  rules?: RegisterOptions<T>;
  error?: string;
  resetValue?: () => void;
  onClearErrors?: ((field: string | null) => void) | null;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  register: UseFormRegister<T>;
  type?: string;
  autoCleanError?: boolean;
  hasSpacingOnFocus?: boolean;
  className?: string;
  disabled?: boolean;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  suffix?: React.ReactNode;
}

const FormField = <T extends FieldValues>({
  name,
  label,
  placeHolder,
  inputClassName,
  rules,
  isAreaInput = false,
  error = "",
  register,
  resetValue,
  onClearErrors = null,
  onChange,
  type = "text",
  autoCleanError = true,
  hasSpacingOnFocus = true,
  className = "",
  disabled = false,
  onBlur,
  suffix,
  ...props
}: FormFieldProps<T>) => {
  const { watch } = useFormContext<T>();
  const value = watch(name);
  const registered = register(name, rules);

  const [inputType, setInputType] = useState<string>(type);
  const [onFocus, setOnFocus] = useState<boolean>(false);

  const handleChange = () => {
    if (autoCleanError && onClearErrors) {
      onClearErrors(name);
    }
  };

  return (
    <div
      className={twMerge(
        "flex flex-col gap-1 items-center w-full relative",
        hasSpacingOnFocus && (value || onFocus) && "mt-3",
        className
      )}
      {...props}
    >
      <div
        className={twMerge(
          "flex justify-between w-full gap-2 border border-gainsboro rounded-[12px]",
          "overflow-hidden items-center focus-within:border-teal-500",
          disabled && "bg-neutral-50 border-gainsboro",
          isAreaInput ? "" : "h-9 px-3",
          error && "border-cinnabar"
        )}
      >
        {isAreaInput ? (
          <textarea // Changed to textarea
            className={twMerge(
              "w-full input-el text-xs resize-none pt-2 px-3 custom-scrollbar text-txtHeading disabled:bg-neutral-50 disabled:border-gainsboro",
              inputClassName
            )}
            placeholder={placeHolder}
            onFocus={() => setOnFocus(true)}
            rows={6}
            {...register(name, rules)}
            onBlur={(e) => {
              registered.onBlur(e);
              setOnFocus(false);
            }}
            onChange={(e) => {
              registered.onChange(e); // Call react-hook-form's onChange
              handleChange(); // Call your custom onChange
              onChange?.(e); // Call the passed onChange function
            }}
            disabled={disabled}
          />
        ) : (
          <input
            className={twMerge(
              "w-full input-el text-xs text-txtHeading disabled:bg-neutral-50 disabled:border-gainsboro",
              inputClassName
            )}
            placeholder={placeHolder}
            onFocus={() => setOnFocus(true)}
            type={inputType}
            {...register(name, rules)}
            onBlur={(e) => {
              registered.onBlur(e);
              setOnFocus(false);
              onBlur?.(e);
            }}
            onChange={(e) => {
              registered.onChange(e); // Call react-hook-form's onChange
              handleChange(); // Call your custom onChange
              onChange?.(e); // Call the passed onChange function
            }}
            disabled={disabled}
          />
        )}
        {!!value && !isAreaInput && type !== "password" && resetValue && (
          <CloseIcon
            data-testid="reset-input-close"
            className="fill-light-gray w-[20px] cursor-pointer"
            onClick={resetValue}
          />
        )}
        {type === "password" && (
          <>
            {inputType === "password" ? (
              <EyeCloseIcon
                data-testid="password-eye-close"
                className="fill-txtBody w-[20px] cursor-pointer"
                onClick={() => {
                  setInputType("text");
                }}
              />
            ) : (
              <EyeOpenIcon
                data-testid="password-eye-open"
                className="fill-txtBody w-[20px] cursor-pointer"
                onClick={() => {
                  setInputType("password");
                }}
              />
            )}
          </>
        )}
        {suffix && <div className="justify-self-end">{suffix}</div>}
      </div>
      {(value || onFocus) && (
        <label
          className={twMerge(
            "pointer-events-none absolute left-2 top-[-10px] mb-0 max-w-[90%] truncate",
            "bg-white px-2 text-[10px] text-txtBody"
          )}
        >
          {label ?? placeHolder}
        </label>
      )}
      {error && (
        <p className="px-2 text-[10px] text-cinnabar self-start">{error}</p>
      )}
    </div>
  );
};

export default FormField;
